import type { Floor } from '@/features/station-mates/api'
import type { Dashboard as WarRoomDashboard, NarrativeGraph } from '@/features/warroom/api'
import type { PortfolioOverviewResponse, TransferAnalysisResponse } from '@/shared/api/types'
import type { AnalyticsSummary as IncidentSummary } from '@/features/incidents/api'
import type { Assay } from '@/features/dossier/api'
import type { SurveyResponseDto } from '@/shared/api/types'

/**
 * The whole platform, in one shape.
 *
 * ## Why every field is nullable and nothing is a `Promise.all`
 *
 * Seven modules, seven rollups, seven ways to be unavailable — a permission the reader does
 * not hold, an election not yet in polling, a rollup that has never run. A dashboard that
 * blanks because one of them 404s is a dashboard nobody trusts, so each source is fetched
 * independently and a missing one is reported as missing rather than drawn as zero. That
 * distinction is the single most important thing on this page: "nothing is wrong here" and
 * "we could not look here" must never render the same way.
 */
export interface Platform {
  floor: Floor | null
  warRoom: WarRoomDashboard | null
  graph: NarrativeGraph | null
  incidents: IncidentSummary | null
  transfers: TransferAnalysisResponse | null
  programmes: PortfolioOverviewResponse | null
  assay: Assay | null
  polls: SurveyResponseDto[] | null
}

/** The one status scale the whole page speaks. Ordered; never reordered by size. */
export type Level = 'critical' | 'serious' | 'watch' | 'steady' | 'unknown'

export const LEVEL_RANK: Record<Level, number> = {
  critical: 0, serious: 1, watch: 2, steady: 3, unknown: 4,
}

/**
 * One domain's state, as a card.
 *
 * `level` is the colour and `headline` is the sentence; a card carries both because colour
 * alone is not a reading and a number alone is not a decision. `unknown` is a real level
 * with its own neutral treatment — see the note on `Platform`.
 */
export interface DomainCard {
  id: string
  label: string
  /** Where clicking goes. */
  to: string
  level: Level
  /** The one figure worth the space. */
  value: string
  /** What that figure is. */
  unit: string
  /** The sentence a reader acts on. */
  headline: string
  /** Small series for the card's sparkline, or empty. */
  series: number[]
  /** Sub-figures shown under the number. */
  facts: { label: string; value: string }[]
}

const num = (n: number | null | undefined) =>
  n == null ? '—' : Math.round(n).toLocaleString()
const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${Math.round(n * 100)}%`
const toNum = (v: string | number | null | undefined): number | null => {
  if (v == null) return null
  const x = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(x) ? x : null
}

export function domains(p: Platform): DomainCard[] {
  const cards: DomainCard[] = []

  // ── polling day turnout ───────────────────────────────────────────────────
  const f = p.floor
  if (!f || f.unavailable || !f.totals) {
    cards.push(unavailable('turnout', 'Turnout', '/station-mates/analytics',
      f?.unavailable ?? 'No election is in its polling window.'))
  } else {
    const t = f.totals
    const share = t.members > 0 ? t.voted / t.members : 0
    const open = f.day.pollsOpen
    // Pace only means something while there is time left to use it — the same rule the
    // station-mates board follows, and the reason this card changes its sentence at close.
    const behind = open ? Math.round(t.members * f.day.elapsedShare) - t.voted : 0
    cards.push({
      id: 'turnout',
      label: 'Turnout',
      to: '/station-mates/analytics',
      level: !open ? 'steady'
        : t.cellsStalled > 0 ? 'critical'
        : behind > t.members * 0.1 ? 'serious'
        : behind > 0 ? 'watch' : 'steady',
      value: pct(share),
      unit: 'of members voted',
      headline: !open
        ? `Polls closed. ${num(t.voted)} of ${num(t.members)} members turned out.`
        : behind > 0
          ? `${num(behind)} votes behind the clock, ${num(t.outstanding)} still to move.`
          : `Ahead of the clock with ${num(t.outstanding)} still to move.`,
      series: f.series.map((b) => b.votedInHour),
      facts: [
        { label: 'Cells behind', value: num(t.cellsBehind) },
        { label: 'Silent', value: num(t.cellsStalled) },
        { label: 'In a queue', value: num(t.inQueue) },
      ],
    })
  }

  // ── social ────────────────────────────────────────────────────────────────
  if (!p.warRoom) {
    cards.push(unavailable('social', 'Social', '/war-room/analytics',
      'The war room dashboard did not return.'))
  } else {
    const w = p.warRoom
    const g = p.graph
    const unanswered = g
      ? g.threads.filter((x) => x.threat != null && x.threat >= g.deployThreshold
          && x.draftCount === 0 && x.deployedCount === 0).length
      : 0
    cards.push({
      id: 'social',
      label: 'Social',
      to: '/war-room/analytics',
      level: unanswered > 0 ? 'critical'
        : w.threatsAboveThreshold > 0 ? 'serious'
        : w.threadsUnscored > w.threadsLive * 0.5 ? 'watch' : 'steady',
      value: num(w.threatsAboveThreshold),
      unit: `above threat ${w.deployThreshold}`,
      headline: unanswered > 0
        ? `${num(unanswered)} hot threads have nothing drafted against them.`
        : w.threadsUnscored > 0
          ? `${num(w.threadsUnscored)} threads have never been read — unknown, not harmless.`
          : `${num(w.threadsLive)} threads watched, nothing above the line.`,
      series: g ? g.platforms.map((x) => x.threads) : [],
      facts: [
        { label: 'Live threads', value: num(w.threadsLive) },
        { label: 'Drafts waiting', value: num(w.draftsAwaiting) },
        { label: 'Coordination', value: num(w.coordinationSignals) },
      ],
    })
  }

  // ── incidents ─────────────────────────────────────────────────────────────
  if (!p.incidents) {
    cards.push(unavailable('incidents', 'Incidents', '/incidents/analytics',
      'The incident rollup did not return.'))
  } else {
    const s = p.incidents
    const critical = s.bySeverity.CRITICAL ?? 0
    cards.push({
      id: 'incidents',
      label: 'Incidents',
      to: '/incidents/analytics',
      level: s.openOlderThan48h > 0 ? 'critical'
        : critical > 0 ? 'serious'
        : s.open + s.investigating > 0 ? 'watch' : 'steady',
      value: num(s.open + s.investigating),
      unit: 'open on the desk',
      headline: s.openOlderThan48h > 0
        ? `${num(s.openOlderThan48h)} have been open past the ageing threshold.`
        : critical > 0
          ? `${num(critical)} critical incidents recorded in this window.`
          : `${num(s.total)} recorded, nothing past the ageing threshold.`,
      series: [],
      facts: [
        { label: 'Critical', value: num(critical) },
        { label: 'Verified', value: s.verifiedShare == null ? '—' : pct(s.verifiedShare) },
        { label: 'Escalated', value: num(s.escalated) },
      ],
    })
  }

  // ── transfers ─────────────────────────────────────────────────────────────
  if (!p.transfers) {
    cards.push(unavailable('transfers', 'Transfers', '/transfers/analysis',
      'The transfer rollup did not return.'))
  } else {
    const d = p.transfers
    const net = (d.byUnit ?? []).reduce((n, u) => n + u.net, 0)
    cards.push({
      id: 'transfers',
      label: 'Transfers',
      to: '/transfers/analysis',
      level: d.successRate != null && d.successRate < 0.5 ? 'serious'
        : d.open > d.recorded ? 'watch' : 'steady',
      value: num(d.recorded),
      unit: 'recorded on our register',
      headline: d.successRate == null
        ? `${num(d.open)} requests in flight, none resolved yet.`
        : `${pct(d.successRate)} of resolved requests landed; net movement ${net >= 0 ? '+' : ''}${num(net)}.`,
      series: (d.recordedDaily ?? []).map((x) => x.count),
      facts: [
        { label: 'Open', value: num(d.open) },
        { label: 'Rejected', value: num(d.rejected) },
        { label: 'Net move', value: `${net >= 0 ? '+' : ''}${num(net)}` },
      ],
    })
  }

  // ── programmes ────────────────────────────────────────────────────────────
  if (!p.programmes) {
    cards.push(unavailable('programmes', 'Programmes', '/programs/analytics/council',
      'The portfolio rollup did not return.'))
  } else {
    const o = p.programmes
    const spend = toNum(o.spendTotal)
    const budget = toNum(o.budgetTotal)
    const burn = budget && budget > 0 && spend != null ? spend / budget : null
    cards.push({
      id: 'programmes',
      label: 'Programmes',
      to: '/programs/analytics/council',
      // The de-duplicated headcount, never the sum of programme rows — the same household
      // in three programmes is one person, and the sum is what an opponent quotes back.
      level: o.unmatchableSubjects > o.distinctSubjects * 0.25 ? 'watch' : 'steady',
      value: num(o.distinctSubjects),
      unit: 'distinct people reached',
      headline: o.unmatchableSubjects > 0
        ? `${num(o.beneficiaryTotal)} enrolments are ${num(o.distinctSubjects)} people; ${num(o.unmatchableSubjects)} could not be matched.`
        : `${num(o.programmes)} programmes reaching ${num(o.distinctSubjects)} people.`,
      series: (o.trend ?? []).map((x) => x.enrolled),
      facts: [
        { label: 'Programmes', value: num(o.programmes) },
        { label: 'Budget used', value: burn == null ? '—' : pct(burn) },
        { label: 'Unmatchable', value: num(o.unmatchableSubjects) },
      ],
    })
  }

  // ── the record ────────────────────────────────────────────────────────────
  if (!p.assay) {
    cards.push(unavailable('evidence', 'Evidence', '/dossier/analytics',
      'The evidence rollup did not return.'))
  } else {
    const s = p.assay.standing
    const solid = s.items > 0 ? s.atOrAboveFloor / s.items : null
    cards.push({
      id: 'evidence',
      label: 'Evidence',
      to: '/dossier/analytics',
      level: solid == null ? 'unknown'
        : solid < 0.4 ? 'serious' : solid < 0.7 ? 'watch' : 'steady',
      value: pct(solid),
      unit: 'strong enough to quote',
      headline: solid == null
        ? 'Nothing on file in this window.'
        : `${num(s.atOrAboveFloor)} of ${num(s.items)} items would survive a challenge.`,
      series: p.assay.latency.map((b) => b.items),
      facts: [
        { label: 'Single source', value: num(s.singleSource) },
        { label: 'Disputed', value: num(s.disputed + s.debunked) },
        { label: 'Sources', value: num(s.sources) },
      ],
    })
  }

  return cards
}

function unavailable(id: string, label: string, to: string, why: string): DomainCard {
  return {
    id, label, to, level: 'unknown', value: '—', unit: 'not available',
    headline: why, series: [], facts: [],
  }
}

/** The worst level present, for the page's own headline. */
export function worst(cards: DomainCard[]): Level {
  return cards.reduce<Level>((acc, c) =>
    (LEVEL_RANK[c.level] < LEVEL_RANK[acc] ? c.level : acc), 'unknown')
}
